import type { DocumentBlock, InlineNode, TreeNode } from "@/api/document/block";

import { MANUAL_PROMPT_VERSION } from "./schema";
import type { ManualContent } from "./schema";

export type ManualKey = "patient" | keyof ManualContent;

interface TerminologyWarning {
	term: string;
	where: string;
	suggestion: string;
}

export interface BlockWarning {
	blockId: string;
	term: string;
	suggestion: string;
}

function inlineText(content: InlineNode[] | undefined) {
	if (content === undefined) {
		return "";
	}

	return content.map((node) => (node.type === "text" ? node.text : "")).join("");
}

interface BannedTerm {
	term: string;
	pattern: RegExp;
	suggestion: string;
	note?: string;
	tolerable?: boolean;
}

interface ManualPage {
	page: number;
	title: string;
	blocks: { title: string; source: ManualKey | null }[];
}

const CONTACT = {
	name: "Dra. Rafaela Nicholls",
	crm: "CRM-MG 54796 | RQE 55737",
	whatsapp: "(31) 98303-5527",
	email: "drarafaela@plastica.ai",
	instagram: "@drarafaelanicholls",
} as const;

const PAGES: ManualPage[] = [
	{ page: 1, title: "Capa", blocks: [{ title: "Capa", source: "patient" }] },
	{
		page: 2,
		title: "Carta + Resumo",
		blocks: [
			{ title: "Carta", source: "letter" },
			{ title: "Resumo Executivo + O Que Foi Indicado", source: "summary" },
		],
	},
	{ page: 3, title: "Suas Queixas", blocks: [{ title: "Suas Queixas", source: "complaints" }] },
	{
		page: 4,
		title: "O Que Avaliamos",
		blocks: [{ title: "O Que Avaliamos", source: "assessment" }],
	},
	{
		page: 5,
		title: "Seu Plano Cirurgico",
		blocks: [{ title: "Seu Plano Cirurgico", source: "plan" }],
	},
	{
		page: 6,
		title: "Seguranca em Primeiro Lugar",
		blocks: [{ title: "Seguranca em Primeiro Lugar", source: "safety" }],
	},
	{
		page: 7,
		title: "Proximos Passos + Cuidados Importantes",
		blocks: [
			{ title: "Proximos Passos", source: "next_steps" },
			{ title: "Cuidados Importantes", source: "risks" },
		],
	},
	{
		page: 8,
		title: "Investimento + Uma Jornada Juntas",
		blocks: [
			{ title: "Investimento", source: "investment" },
			{ title: "Uma Jornada Juntas + Contato", source: "journey" },
			{ title: "Pagina final", source: null },
		],
	},
];

const PARAPHRASE_PATH = /(?:^|\.)complaints\[\d+\]\.paraphrase$/u;

const CLINICAL_SWAPS: BannedTerm[] = [
	{ term: "peito(s)", pattern: /\bpeitos?\b/iu, suggestion: 'usar "mama(s)"' },
	{ term: "lipo isolado", pattern: /\blipo\b/iu, suggestion: 'usar "lipoaspiracao"' },
	{
		term: "lipar",
		pattern: /\blipa(?:r|ndo|d[oa]s?)\b/iu,
		suggestion: 'usar "lipoaspirar/lipoaspirado"',
	},
	{
		term: "subido",
		pattern: /\bsubid[oa]s?\b/iu,
		suggestion: 'usar "elevado e reposicionado"',
		note: "quando descreve pubis/tecido",
	},
	{
		term: "casquinhada",
		pattern: /\bcasquinhadas?\b/iu,
		suggestion: 'usar "retirada de tecido mamario em excesso"',
	},
	{ term: "empezinha", pattern: /\bempezinhas?\b/iu, suggestion: 'usar "projetado"' },
	{
		term: "caida(s)",
		pattern: /\bca[ií]d[oa]s?\b/iu,
		suggestion: 'usar "flacida(s)"',
		note: "quando descreve mamas",
	},
	{
		term: "mole",
		pattern: /\bmoles?\b/iu,
		suggestion: 'usar "flacida", "com pouca firmeza" ou "tecido predominantemente gorduroso"',
		note: "quando descreve mamas/tecido",
	},
	{
		term: "makeover sozinho",
		pattern: /(?<!mommy\s)\bmakeover\b/iu,
		suggestion: 'usar "mommy makeover" por extenso',
	},
	{ term: "abdomen", pattern: /\babd[oô]men\b/iu, suggestion: 'usar "abdome"' },
	{
		term: "limpeza",
		pattern: /\blimpezas?\b/iu,
		suggestion: 'usar "lipoaspiracao"',
		note: "quando significa lipo",
	},
	{
		term: "secar",
		pattern: /\bseca(?:r|ndo)\b/iu,
		suggestion: 'usar "reduzir" ou "lipoaspirar"',
		note: "quando significa lipo",
	},
	{ term: "tirsepatida", pattern: /tirsepatida/iu, suggestion: 'usar "tirzepatida"' },
	{
		term: "pochete",
		pattern: /\bpochetes?\b/iu,
		suggestion: 'usar "abdome inferior" na avaliacao fisica',
		note: 'na parafrase de queixa "pochete" e aceitavel',
		tolerable: true,
	},
	{
		term: "volumoso acentuado",
		pattern: /\bvolum[óò]s[oa]s?\b/iu,
		suggestion: 'usar "volumoso" sem acento',
	},
	{ term: "Swiffer", pattern: /\bswiffer\b/iu, suggestion: 'usar "Safer"' },
];

const DIMINUTIVES: BannedTerm[] = [
	{ term: "menorzinha", pattern: /\bmenorzinh[oa]s?\b/iu, suggestion: 'usar "menor"' },
	{ term: "maiszinha", pattern: /\bmaiszinh[oa]s?\b/iu, suggestion: 'usar "mais"' },
	{ term: "baixinha", pattern: /\bbaixinh[oa]s?\b/iu, suggestion: 'usar "baixa"' },
	{ term: "curvinha", pattern: /\bcurvinh[oa]s?\b/iu, suggestion: 'usar "curva" ou "contorno"' },
	{ term: "diferencinha", pattern: /\bdiferencinhas?\b/iu, suggestion: 'usar "diferenca"' },
	{ term: "pouquinho", pattern: /\bpouquinh[oa]s?\b/iu, suggestion: 'usar "pouco"' },
	{ term: "tempinho", pattern: /\btempinhos?\b/iu, suggestion: 'usar "tempo"' },
	{ term: "pertinho", pattern: /\bpertinh[oa]s?\b/iu, suggestion: 'usar "proximo"' },
];

const TEAM_NAMES: BannedTerm[] = [
	{
		term: "Tai",
		pattern: /\bta[ií](?!\p{L})/iu,
		suggestion: 'a assistente e sempre "Thaiane", nunca "Tai"',
	},
];

const ALLOWED_DIMINUTIVES = ["bumbum", "redondinho"] as const;

class ManualRulebook {
	readonly promptVersion = MANUAL_PROMPT_VERSION;
	readonly pageMap = PAGES;
	readonly doctorContact = CONTACT;
	readonly allowedTerms = ALLOWED_DIMINUTIVES;

	private readonly bannedTerms = [...CLINICAL_SWAPS, ...DIMINUTIVES, ...TEAM_NAMES];

	readonly doctorRules = this.composeDoctorRules();

	lint(content: unknown) {
		const warnings: TerminologyWarning[] = [];

		for (const field of this.flatten(content, "")) {
			for (const banned of this.scanText(field.text, PARAPHRASE_PATH.test(field.path))) {
				warnings.push({ term: banned.term, where: field.path, suggestion: banned.suggestion });
			}
		}

		return warnings;
	}

	lintBlocks(blocks: DocumentBlock[]) {
		const warnings: BlockWarning[] = [];

		for (const block of blocks) {
			this.lintNode(block, warnings);
		}

		return warnings;
	}

	private lintNode(node: TreeNode, warnings: BlockWarning[]) {
		if (
			node.type === "page-break" ||
			node.type === "image" ||
			node.type === "signature" ||
			node.type === "signature-row" ||
			node.type === "signature-slot"
		) {
			return;
		}

		if (
			node.type === "page" ||
			node.type === "variant-card" ||
			node.type === "table" ||
			node.type === "table-row" ||
			node.type === "pattern"
		) {
			for (const child of node.content) {
				this.lintNode(child, warnings);
			}

			return;
		}

		const title = node.type === "step" ? node.attrs.title : "";
		const text = `${title}\n${inlineText(node.content)}`;
		const tolerate = node.type === "paragraph" && node.attrs.emphasis === "paraphrase";

		for (const banned of this.scanText(text, tolerate)) {
			warnings.push({ blockId: node.attrs.id, term: banned.term, suggestion: banned.suggestion });
		}
	}

	private scanText(text: string, tolerateParaphrase: boolean) {
		return this.bannedTerms.filter((banned) => {
			if (tolerateParaphrase && banned.tolerable === true) {
				return false;
			}

			return banned.pattern.test(text);
		});
	}

	private flatten(value: unknown, path: string): { path: string; text: string }[] {
		if (typeof value === "string") {
			return [{ path, text: value }];
		}

		if (Array.isArray(value)) {
			return value.flatMap((item, index) => this.flatten(item, `${path}[${index}]`));
		}

		if (typeof value === "object" && value !== null) {
			return Object.entries(value).flatMap(([key, sub]) =>
				this.flatten(sub, path === "" ? key : `${path}.${key}`),
			);
		}

		return [];
	}

	private terminologyTable() {
		return this.bannedTerms
			.map(
				(banned) =>
					`- "${banned.term}" -> ${banned.suggestion}${banned.note === undefined ? "" : ` (${banned.note})`}`,
			)
			.join("\n");
	}

	private composeDoctorRules() {
		return `# REGRAS DO "MANUAL DA MINHA CIRURGIA" - ${CONTACT.name}

## 0. REGRA DE OURO - identidade do documento
O manual deve parecer oriundo das anotacoes e da memoria atenta e acolhedora da doutora, e NAO parecer transcricao analisada por uma IA. Respeitoso, proximo, tecnico e sobrio. Simule que a Dra. sentou, relembrou a consulta e escreveu de proprio punho: paragrafos fluidos em vez de bullets excessivos, sintetizar em vez de transcrever, nivel de detalhe igual ao que uma medica atenta anotaria.

## 1. FIDELIDADE (zero invencao)
Extrair APENAS o que consta na transcricao. NAO inventar diagnosticos, medidas, valores; NAO inferir contexto plausivel; NAO completar raciocinios; NAO misturar dados de outras pacientes. Nao discutido -> "[A definir na consulta presencial]". Ambiguo -> "[Confirmar com a Dra.]". Mencao a terceiros nao significa presenca na consulta; so incluir acompanhante com confirmacao explicita. Indicacao so entra como resposta exata a pergunta "quem te indicou?".

## 2. TOM E VOZ
Voz da Dra. em primeira pessoa; paciente em segunda pessoa. Nunca terceira pessoa, nunca frio/clinico, nunca comercial. "Nos" e "juntas" para parceria. Travessao e PERMITIDO e bem-vindo neste documento (regra oposta a das mensagens de WhatsApp; sao dois canais distintos).

## 3. LINGUAGEM - parafrase e terminologia
Nunca transcrever falas literais da paciente; interpretar em linguagem tecnica acessivel. Termos proibidos e substituicoes:
${this.terminologyTable()}
Diminutivos proibidos em geral. Excecao: ${ALLOWED_DIMINUTIVES.map((term) => `"${term}"`).join(" e ")} sao aceitaveis. A assistente e sempre "Thaiane".

## 4. CONTEUDO MEDICO
Avaliacao cardiologica obrigatoria (nao "pre-anestesica"); hospital com UTI obrigatorio; trombose com bota pneumatica, meias compressivas e mobilizacao precoce; seroma com dreno, cinta e drenagem linfatica. Avaliacao presencial pelo menos uma vez antes da cirurgia. Nao incluir sequencia cirurgica (informacao interna). Exames de imagem por tipo de cirurgia. Retorno ao trabalho minimo de 20 dias. Conteudo proibido: valores especificos de orcamento, nota fiscal, saude mental/medicamentos psiquiatricos, detalhes operacionais de hospitais.

## 5. CARTA PESSOAL
"Querida [Nome]," + prazer da consulta e indicacao confirmada + compreensao das queixas/parceria + introducao ao documento + card rose "Estou aqui com voce em cada etapa desta jornada." + convite a leitura + "Com carinho," + nome + ${CONTACT.crm}. Nunca incluir incidentes tecnicos da chamada, familiares vistos so na ligacao, rotina/comercio da paciente, nem fatos biograficos da Dra.

## 6. SECOES
Queixas: parafrase interpretada, nunca citacao literal; 3-5 queixas. Plano: cada procedimento com titulo e 2-3 paragrafos fluidos, proibido micro-itemizar. Investimento: nunca valores especificos; bonus itemizado sempre; sempre "o que NAO esta incluso" (drenagens + medicamentos pos-op). Proximos passos: cards com titulo sem numeracao (a numeracao e automatica), o que a paciente ja entregou nao vira passo. Jornada: paragrafo com o nome + frase inspiracional especifica + contato. Riscos: gerais e especificos, tom educativo nunca alarmista.

## 7. CONTATO DA DRA. (sempre em "Uma Jornada Juntas")
WhatsApp ${CONTACT.whatsapp} - ${CONTACT.email} - ${CONTACT.instagram} - ${CONTACT.crm}.`;
	}
}

export const ManualRules = new ManualRulebook();
